// The playground's API client (PG-1, planner/lender-hub/playground-api-client-plan.md): saved requests per lender, the
// built-container target (POST /admin/v1/lenders/{slug}/try, the same call as the Hub address with x-hub-key, the key
// stays on the Hub), the input check and "Copy as curl". The sandbox target runs through the playground's agent
// (agent.run). On the fixtures (npm test) the saved requests live in memory and the built container is not there.
import type { z } from 'zod'
import { ApiRequest, StandardInput } from '@/contract/schemas'
import { api, apiMode } from '@/lib/hubApi'
import type { RunResult } from '@/lib/agent'
import { HUB_KEY_HEADER, curlFor, endpointBody, endpointUrl } from '@/lib/urls'

export type SavedRequest = z.infer<typeof ApiRequest>
export type ClientEnv = 'uat' | 'production'
export type FakeReply = { status: number; body: unknown }
export interface TryResult { result: RunResult; version: string; environment: ClientEnv; address: string; container: string }

/** A request as the screen holds it: the JSON as typed, parsed only on Send / Save. */
export interface Draft {
  endpoint: string
  name: string
  input: string
  prev: string
  /** a typed lender reply (sandbox only); null = the real lender */
  reply: { status: string; body: string } | null
  /** the saved request it was opened from */
  savedId: string | null
}

const pretty = (v: unknown) => JSON.stringify(v, null, 2)
export const draftFrom = (endpoint: string, input: Record<string, unknown>, prev: Record<string, unknown> | null): Draft => ({ endpoint, name: '', input: pretty(input), prev: prev ? pretty(prev) : '', reply: null, savedId: null })
export const draftOf = (r: SavedRequest): Draft => ({ endpoint: r.endpoint, name: r.name, input: pretty(r.input), prev: r.prev ? pretty(r.prev) : '', reply: r.reply ? { status: String(r.reply.status), body: pretty(r.reply.body) } : null, savedId: r.id })

export interface Parsed { input: Record<string, unknown>; prev: Record<string, unknown> | null; reply: FakeReply | null }

/** The draft's JSON parsed, or the first thing wrong with it in plain words. */
export function parseDraft(d: Draft): { ok: true; value: Parsed } | { ok: false; error: string } {
  const obj = (text: string, what: string, empty: Record<string, unknown> | null) => {
    if (!text.trim()) return { v: empty }
    try {
      const v: unknown = JSON.parse(text)
      if (v === null && empty === null) return { v: null }
      if (!v || typeof v !== 'object' || Array.isArray(v)) return { e: `${what} must be a JSON object { … }` }
      return { v: v as Record<string, unknown> }
    } catch (err) {
      return { e: `${what} is not valid JSON: ${(err as Error).message}` }
    }
  }
  const input = obj(d.input, 'Input', {})
  if ('e' in input) return { ok: false, error: input.e! }
  const prev = obj(d.prev, 'prev', null)
  if ('e' in prev) return { ok: false, error: prev.e! }
  let reply: FakeReply | null = null
  if (d.reply) {
    const status = Number(d.reply.status)
    if (!Number.isInteger(status) || status < 0 || status > 599) return { ok: false, error: 'Lender reply status: 0 (no reply) to 599' }
    let body: unknown = null
    if (d.reply.body.trim()) {
      try { body = JSON.parse(d.reply.body) } catch (err) { return { ok: false, error: `Lender reply body is not valid JSON: ${(err as Error).message}` } }
    }
    reply = { status, body }
  }
  return { ok: true, value: { input: input.v as Record<string, unknown>, prev: prev.v as Record<string, unknown> | null, reply } }
}

/** What the standard input (G10) says about this input: [] = it is one the website / app could send. */
export function inputProblems(input: Record<string, unknown>): string[] {
  const r = StandardInput.strict().safeParse(input)
  return r.success ? [] : r.error.issues.map((i) => `${i.path.join('.') || 'input'}: ${i.code === 'unrecognized_keys' ? `not a standard field (${(i as { keys?: string[] }).keys?.join(', ')})` : i.message}`)
}

/** The curl someone can paste: the Hub address of the environment, the key as a placeholder. */
export const curlOf = (base: string, slug: string, route: string, p: Parsed) => curlFor('POST', endpointUrl(base, slug, route), endpointBody(p.input, p.prev), HUB_KEY_HEADER)

// ---- saved requests: the Hub's (shared by the team); in memory on the fixtures ----------------------------------

const memory: SavedRequest[] = []
const newId = () => `rq_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`

export const requests = {
  async list(slug: string): Promise<SavedRequest[]> {
    if (apiMode()) return api<SavedRequest[]>('GET', `/lenders/${slug}/requests`)
    return memory.filter((r) => r.lenderSlug === slug)
  },
  async save(slug: string, body: { endpoint: string; name: string } & Parsed, by: string): Promise<SavedRequest> {
    if (apiMode()) return api<SavedRequest>('POST', `/lenders/${slug}/requests`, body)
    const at = memory.findIndex((r) => r.lenderSlug === slug && r.endpoint === body.endpoint && r.name === body.name)
    const rec: SavedRequest = { id: at >= 0 ? memory[at]!.id : newId(), lenderSlug: slug, endpoint: body.endpoint, name: body.name, input: body.input, prev: body.prev, reply: body.reply, savedBy: by, savedAt: new Date().toISOString() }
    if (at >= 0) memory[at] = rec
    else memory.push(rec)
    return rec
  },
  async remove(id: string): Promise<void> {
    if (apiMode()) return void (await api('DELETE', `/requests/${id}`))
    const at = memory.findIndex((r) => r.id === id)
    if (at >= 0) memory.splice(at, 1)
  },
  /** for the tests */
  reset: () => void memory.splice(0),
}

/**
 * The built container: what the Hub address of this environment runs (UAT = the newest built, production = live).
 * BP-1: `productSlug` = that product's batch; null = the lender's whole-lender batch.
 */
export async function tryOnContainer(slug: string, route: string, environment: ClientEnv, p: Parsed, productSlug: string | null = null): Promise<TryResult> {
  if (!apiMode()) throw new Error('The built container needs the Lender Hub API (the fixtures have no runner).')
  return api<TryResult>('POST', `/lenders/${slug}/try`, { route, environment, input: p.input, prev: p.prev, ...(productSlug ? { productSlug } : {}) })
}
